import React, { useState, useEffect } from 'react';
import { Terminal, Power, Shield, Menu, X } from 'lucide-react';

const Navbar = ({ currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch('https://api.4fingerstudios.com/api/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setIsAuthenticated(true);
            setIsAdmin(userData.role === 'Admin');
          } else {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
        } catch (error) {
          setIsAuthenticated(false);
          setIsAdmin(false);
          console.error('Auth check failed:', error);
        }
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };
    checkAuth();
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const baseNavItems = [
    { icon: <Terminal size={20} />, href: '/', title: 'HOME' },
    { icon: <Power size={20} />, href: isAuthenticated ? '/user-profile' : '/Profile', title: isAuthenticated ? 'PROFILE' : 'LOGIN' }
  ];

  const navItems = isAdmin
    ? [...baseNavItems.slice(0, -1), { icon: <Shield size={20} />, href: '/admin', title: 'ADMIN' }, baseNavItems[baseNavItems.length - 1]]
    : baseNavItems;

  return (
    <nav className="fixed top-0 w-full z-50 bg-black/95 backdrop-blur-xl border-b border-red-500/20 shadow-2xl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between py-4 sm:py-6 px-4 sm:px-8">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <img 
              src="https://cdn.4fingerstudios.com/4FS_Full_Logo.png" 
              alt="4FingerStudios Logo" 
              className="h-8 sm:h-12 w-auto hover:opacity-80 transition-all duration-300"
            />
            <div className="hidden sm:block h-8 w-px bg-red-500/30"></div>
            <span className="text-xs sm:text-sm font-semibold text-red-400 uppercase tracking-wider">
              {currentPage}
            </span>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-red-400 hover:text-red-300 transition-all duration-300 p-2 rounded-lg hover:bg-red-500/10 touch-manipulation"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="hidden md:block">
            <ul className="flex items-center space-x-8">
              {navItems.map((item, index) => (
                <li
                  key={index}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className="group relative"
                >
                  <a href={item.href} className="flex flex-col items-center">
                    <div className={`p-3 transition-all duration-300 rounded-xl group-hover:scale-105
                      ${index === activeIndex 
                        ? 'bg-red-500/20 text-red-300 shadow-lg shadow-red-500/20' 
                        : 'text-white/70 hover:text-red-300 hover:bg-red-500/10'
                      }`}
                    >
                      {item.icon}
                    </div>
                    <span
                      className={`absolute -bottom-6 text-xs font-semibold tracking-wide
                        transition-all duration-300 transform
                        ${index === activeIndex 
                          ? 'opacity-100 translate-y-0 text-red-300' 
                          : 'opacity-0 -translate-y-2 text-red-400/60'
                        }
                      `}
                    >
                      {item.title}
                    </span>
                  </a>
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5
                    bg-gradient-to-r from-red-500 to-purple-500 rounded-full transition-all duration-300
                    ${index === activeIndex ? 'w-full' : 'w-0'}`}
                  ></div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 top-0 left-0 w-full h-full bg-black/80 z-40" 
               onClick={() => setIsMenuOpen(false)} />
        )}

        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 w-full transition-all duration-300 ease-in-out overflow-hidden border-t border-red-500/20 z-50
          ${isMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className="bg-black/95 backdrop-blur-xl p-4 sm:p-6 pt-6 sm:pt-8 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-white">Navigation Menu</h3>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-red-400 hover:text-red-300 transition-all duration-300 p-2 rounded-lg hover:bg-red-500/10 touch-manipulation"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>
            <ul className="flex flex-col space-y-2 sm:space-y-4">
              {navItems.map((item, index) => (
                <li key={index} className="transform transition-all duration-300"
                  style={{ transitionDelay: `${index * 50}ms` }}>
                  <a
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 text-white/80 hover:text-red-300 rounded-xl hover:bg-red-500/10 transition-all duration-300 hover:scale-105 border border-red-500/10 hover:border-red-500/30 active:scale-95 touch-manipulation min-h-[48px]"
                  >
                    <div className="p-2 rounded-lg bg-red-500/10 group-hover:bg-red-500/20 transition-all duration-300 flex-shrink-0">
                      {item.icon}
                    </div>
                    <span className="font-semibold text-sm sm:text-base">{item.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            
            {/* Mobile Menu Footer */}
            <div className="mt-6 pt-4 border-t border-red-500/20">
              <div className="text-center">
                <p className="text-xs text-red-400/60">
                  © {new Date().getFullYear()} 4FingerStudios
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

